import { PaymentMethod } from '../models/Organization';
import { userApiClient } from '../userApiClient';
import { getPassCheckoutErrorCode, setPassCheckoutPendingError } from './passCheckoutErrors';

const STORAGE_KEY = 'passPurchaseResume';

export interface PassPurchaseResumePayload {
  passTemplateId: string;
  returnPath: string;
  paymentMethod: PaymentMethod;
}

/** Stashed right before redirecting an anonymous visitor to /login from a Buy button. */
export function setPassPurchaseResume(payload: PassPurchaseResumePayload): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('Failed to store pass purchase resume payload:', error);
  }
}

export function getPassPurchaseResume(): PassPurchaseResumePayload | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.passTemplateId === 'string' &&
      typeof parsed.returnPath === 'string' &&
      (parsed.paymentMethod === PaymentMethod.CASH || parsed.paymentMethod === PaymentMethod.ONLINE)
    ) {
      return parsed;
    }
    // Legacy stash without paymentMethod — treat as ONLINE (pre-cash checkout).
    if (parsed && typeof parsed.passTemplateId === 'string' && typeof parsed.returnPath === 'string') {
      return {
        passTemplateId: parsed.passTemplateId,
        returnPath: parsed.returnPath,
        paymentMethod: PaymentMethod.ONLINE,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function clearPassPurchaseResume(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Resumes a pass purchase stashed before an anonymous Buy click redirected to /login.
 * Online → Stripe redirect; cash → pending confirmation route.
 */
export async function resumePassPurchaseAfterLogin(): Promise<boolean> {
  const payload = getPassPurchaseResume();
  if (!payload) return false;
  clearPassPurchaseResume();

  try {
    const response = await userApiClient.checkoutPass({
      passTemplateId: payload.passTemplateId,
      paymentMethod: payload.paymentMethod,
    });
    if (payload.paymentMethod === PaymentMethod.CASH) {
      window.location.href = '/passes/purchase/pending';
      return true;
    }
    if (response.stripeCheckoutUrl) {
      window.location.href = response.stripeCheckoutUrl;
      return true;
    }
    setPassCheckoutPendingError('generic');
    return false;
  } catch (error) {
    console.error('Error resuming pass purchase after login:', error);
    setPassCheckoutPendingError(getPassCheckoutErrorCode(error));
    return false;
  }
}
